import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api, assetUrl } from "@/lib/api";
import { applySeo } from "@/lib/utils";
import { Reveal } from "@/components/site/Reveal";

export default function JournalDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const j = t.journal;
  const [essay, setEssay] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setEssay(null);
    setNotFound(false);
    api.get(`/essays/${slug}`).then((r) => setEssay(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!essay) return;
    const desired = (lang === "ro" ? essay.slug_ro : essay.slug_en) || essay.slug;
    if (desired && desired !== slug) navigate(`/journal/${desired}`, { replace: true });
  }, [lang, essay, slug, navigate]);

  useEffect(() => {
    if (!essay) return;
    const en = `/journal/${essay.slug_en || essay.slug}`;
    const ro = `/journal/${essay.slug_ro || essay.slug}`;
    applySeo({
      title: essay[`seo_title_${lang}`] || `${g("title")} — Oana Mudura`,
      description: essay[`meta_description_${lang}`] || g("excerpt"),
      path: window.location.pathname,
      enPath: en,
      roPath: ro,
    });
  }, [essay, lang]);

  const g = (k) => (essay ? essay[`${k}_${lang}`] || essay[`${k}_ro`] || "" : "");

  if (notFound)
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center gap-6">
        <p className="eyebrow text-ink/50">404</p>
        <Link to="/journal" className="eyebrow underline">{j.backCta}</Link>
      </div>
    );

  if (!essay) return <div className="min-h-screen bg-paper flex items-center justify-center eyebrow text-ink/40">{j.loading}</div>;

  const paras = g("body").split(/\n\n+/).filter(Boolean);

  return (
    <div className="bg-paper text-ink min-h-screen">
      <article className="pt-32 md:pt-40 pb-32">
        <div className="px-6 md:px-12 lg:px-20 max-w-4xl mx-auto">
          <Link to="/journal" data-testid="essay-back" data-cursor="link" className="eyebrow inline-flex items-center gap-2 text-ink/50 hover:text-ink transition-colors mb-14">
            <ArrowLeft size={14} /> {j.backCta}
          </Link>

          <div className="flex items-center gap-4 eyebrow text-ink/45 mb-6">
            <span>{g("category")}</span>
            <span className="w-8 h-px bg-ink/25" />
            <span className="tnum">{essay.reading_time} {j.readingLabel}</span>
          </div>

          <h1 className="serif text-5xl md:text-7xl leading-[1.02] mb-10">{g("title")}</h1>
          <p className="serif italic text-2xl md:text-3xl leading-[1.25] text-ink/70 mb-12">{g("excerpt")}</p>
        </div>

        {essay.cover_image && (
          <Reveal>
            <div className="px-6 md:px-12 lg:px-20 mb-14 md:mb-20">
              <div className="overflow-hidden aspect-[16/9] max-w-6xl mx-auto">
                <img src={assetUrl(essay.cover_image)} alt={g("title")} className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        )}

        <div className="px-6 md:px-12 lg:px-20 max-w-2xl mx-auto space-y-7">
          {paras.map((p, i) => (
            <p
              key={i}
              className={`text-lg md:text-xl leading-[1.75] text-ink/85 ${
                i === 0 ? "first-letter:serif first-letter:text-6xl first-letter:font-semibold first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8] first-letter:mt-1" : ""
              }`}
            >
              {p}
            </p>
          ))}
        </div>

        <div className="px-6 md:px-12 lg:px-20 max-w-2xl mx-auto mt-20 pt-10 border-t border-ink/15">
          <Link to="/journal" data-cursor="link" className="eyebrow inline-flex items-center gap-2 hover:opacity-70 transition-opacity">
            <ArrowLeft size={14} /> {j.backCta}
          </Link>
        </div>
      </article>
    </div>
  );
}
